import { VoiceComposer } from '@orborix/voice-composer';
import { Underline } from '../../components/doodles/Doodles.js';
import { DrillModal } from './messages/index.js';
import { ConversationMessages } from './ConversationMessages.js';
import { ConnectionBanner } from './ConnectionBanner.js';
import { SessionClosingCard, SessionEndedCard } from './SessionEndedCard.js';
import { ReplyFailedCard } from './ReplyFailedCard.js';
import { SettingsSheet } from './controls/SettingsSheet.js';
import { PrivateBadge } from './controls/PrivateBadge.js';
import { SpeedQuickControl } from './controls/SpeedQuickControl.js';
import { InspireButton, InspireCard } from './controls/Inspire.js';
import { AssistedCard } from './assisted/AssistedCard.js';
import { AssistedIntroSheet } from './assisted/AssistedIntroSheet.js';
import { A0Suggestions } from './controls/A0Suggestions.js';
import { DirectionMenu } from './controls/SeedMenu.js';
import { SecretWordCard } from './controls/SecretWordCard.js';
import { GameModeChip } from './controls/GameModeChip.js';
import { railTopic } from './controls/railTopic.js';
import { RailMeta } from './controls/RailMeta.js';
import { TapWordHint } from './controls/TapWordHint.js';
import { CoachAvatar } from './controls/CoachPicker.js';
import { JumpToLatestChip } from './controls/atoms.js';
import { IconChatGear, IconPlus, IconSpeaker, IconSpeakerOff, IconX, IconPen, IconWaveSmall } from './icons.js';
import {
  COMPOSER_LABELS,
  COMPOSER_PLACEHOLDER,
  COMPOSER_PLACEHOLDER_ENDED,
  COMPOSER_PLACEHOLDER_TEXT_ONLY,
} from './composerLabels.js';
import { buildErrors, filterErrorsByLevel } from './errorModel.js';
import { FALLBACK_VOICE_ID } from './conversationTypes.js';
import { ExamProgress } from './ExamProgress.js';
import { CelebrationLayer } from '../../lib/celebrations/index.js';
import { useSkillCompletionTick } from './hooks/useSkillCompletionTick.js';
import { MissionTracker } from '../scenarios/MissionTracker.js';
import { UnitObjectivesTracker } from './UnitObjectivesTracker.js';
import { MissionFinishFab } from '../scenarios/MissionFinishFab.js';
import { useEnglishLevel } from '../leveling/useEnglishLevel.js';
import { useEffect, useRef, type CSSProperties } from 'react';
import { ConversationCoachmarks } from '../../components/coachmark/ConversationCoachmarks.js';
import { ShortLeaveSheet } from './ShortLeaveSheet.js';
import type { UseConversationController } from './useConversationController.js';
// Base del chrome de escritorio (.app.dt-app, .dt-side, .dt-main, .dt-icon-btn…).
// La CSS específica de la conversación de escritorio vive en conversation.css.
import '../../components/desktop/desktop.css';
import {
  ShipPieceTracker,
  conversationLive,
  conversationPiece,
} from '../home/ship/ShipPieceTracker.js';
import { useInspireReadsAloud } from '../home/ship/useShip.js';

interface ConversationDesktopProps {
  ctrl: UseConversationController;
}

/**
 * Layout de escritorio de la conversación: rail izquierdo (salir + coach +
 * errores en vivo) + columna principal (header → chat → mic bar). Shell
 * presentacional puro: reusa el controller, `ConversationMessages` y los mismos
 * leaf que la variante móvil (CoachCard/UserBubble/VoiceComposer/SettingsSheet…).
 */
export function ConversationDesktop({ ctrl }: ConversationDesktopProps) {
  const {
    settings,
    correctionLevelEff,
    setSettings,
    setGlobalIntensity,
    speedEff,
    coachMuted,
    toggleCoachMuted,
    muteNotice,
    micHidden,
    recorder,
    freeChat,
    assistedPaused,
    freeStatus,
    holdDisabled,
    coach,
    inspire,
    assisted,
    sendComposerText,
    sendInspireSuggestion,
    drillErr,
    setDrillErr,
    settingsOpen,
    setSettingsOpen,
    incognito,
    toggleIncognito,
    threadRef,
    threadUnpinned,
    handleThreadScroll,
    jumpToLatest,
    state,
    isUserTurn,
    connectionStatus,
    reconnect,
    messages,
    sessionId,
    pauseMainCapture,
    resumeMainCapture,
    leave,
    requestLeave,
    shortLeavePrompt,
    confirmLeaveShort,
    dismissLeaveShort,
    examMode,
    scenarioMission,
    completedObjectives,
    justObjectiveId,
    practicedObjectives,
    justPracticedId,
    allObjectivesDone,
    planLabel,
    planObjectives,
    completedPlanObjectives,
    justPlanObjectiveIdx,
    allPlanObjectivesDone,
    subUnit,
    subUnitObjectives,
    subUnitReps,
    subUnitCounts,
    subUnitCompleted,
    subUnitReview,
    justSubObjIdx,
    subRepTick,
    seedMenuOpen,
    activeSeed,
    seedDisabledReason,
    openSeedMenu,
    closeSeedMenu,
    selectSeed,
    clearSeed,
    selectNews,
    goToScenarios,
    secretWord,
    rerollSecretWord,
    a0Mission,
    a0SuggestionStep,
    sendA0Suggestion,
    a0CelebrateTick,
    serverEnded,
    serverClosing,
    resumeConversation,
    replyFailure,
    retryFailedReply,
    gameArena,
  } = ctrl;
  const shipPiece = conversationPiece({
    examMode,
    a0Mission: ctrl.a0Mission,
    scenarioLabel: ctrl.scenarioLabel,
    planLabel: ctrl.planLabel,
    activeSeed: ctrl.shipSeed,
  });
  // La Cabina desde A1: la frase de «Inspirar» queda a la vista hasta que el alumno habla (card #603).
  const inspireReadAloud = useInspireReadsAloud(shipPiece);
  const { close: closeInspire } = inspire;
  useEffect(() => {
    if (inspireReadAloud && state === 'thinking') closeInspire();
  }, [inspireReadAloud, state, closeInspire]);
  // A skill completed in free talk (W6): small sparkles, no sound (D7).
  const skillCompleteTick = useSkillCompletionTick(ctrl.messages);
  const ended = serverEnded != null || ctrl.lessonCompletion?.phase === 'closing' || ctrl.lessonCompletion?.phase === 'closed';
  // Una vez por parte confirmada, sólo en vivo: un cierre restaurado al
  // reconectar llega `closed` y no se vuelve a festejar.
  const lessonEndTrigger = ctrl.lessonCompletion?.phase === 'closing';

  // Nivel CEFR real del user para el pill "NIVEL" del coach card (cacheado por
  // sessionCache → suele venir del cache, sin re-fetch). En charla A0 muestra "A0".
  const { state: levelState } = useEnglishLevel();

  const activeCoach = coach.coaches.find((c) => c.id === coach.selectedId) ?? coach.coaches[0] ?? null;

  // Conteo de errores en vivo (respeta los toggles de corrección/fonética).
  const allErrors = settings.correction
    ? messages.flatMap((m) =>
        m.who === 'user'
          ? filterErrorsByLevel(
              buildErrors(m).filter((e) => settings.phonetic || e.kind !== 'phonetic'),
              correctionLevelEff,
            )
          : [],
      )
    : [];
  const gramN = allErrors.filter((e) => e.kind === 'grammar').length;
  const phonN = allErrors.filter((e) => e.kind === 'phonetic').length;
  const userTurns = messages.filter((m) => m.who === 'user').length;
  const topic = railTopic(scenarioMission, activeSeed);
  const rootRef = useRef<HTMLDivElement>(null);

  return (
    <div className={`app dt-app bubbles-${settings.density} ${ended ? 'conv-ended' : ''}`} ref={rootRef}>
      {/* Coach-marks de onboarding contextual (primera charla estándar). */}
      <ConversationCoachmarks containerRef={rootRef} ctrl={ctrl} />
      {/* Rail izquierdo */}
      <aside className="dt-side dt-side-conv">
        <button type="button" className="dt-back" onClick={requestLeave}>
          <IconX size={18} />
          <span>Salir de la sesión</span>
        </button>

        {/* Coach activo */}
        <div className="dt-coach-card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {activeCoach ? (
              <CoachAvatar coach={activeCoach} size={44} />
            ) : (
              <div className="dt-coach-avatar-fallback" />
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="tag" style={{ color: 'var(--lime)' }}>
                COACH · LIVE
              </div>
              <div className="dt-coach-name">{activeCoach?.name ?? 'Tu coach'}</div>
              {activeCoach && (
                <div className="dt-coach-meta">
                  {activeCoach.tag} · {activeCoach.region}
                </div>
              )}
            </div>
          </div>

          <RailMeta level={levelState?.cefrGlobal ?? '—'} turns={userTurns} topic={topic} />
        </div>

        {/* Errores en vivo */}
        <div className="dt-errors-panel">
          <div className="tag" style={{ color: 'var(--text-muted)', marginBottom: 10 }}>
            ERRORES EN VIVO
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div className="dt-err-row" style={{ borderColor: 'rgba(248,113,113,0.25)', background: 'rgba(248,113,113,0.06)' }}>
              <div className="dt-err-dot" style={{ background: '#f87171' }}>
                <IconPen size={9} />
              </div>
              <span style={{ flex: 1, color: '#fca5a5', fontFamily: 'JetBrains Mono, monospace', fontSize: 12 }}>
                Gramática
              </span>
              <span style={{ color: '#fca5a5', fontFamily: 'JetBrains Mono, monospace', fontSize: 13, fontWeight: 600 }}>
                {gramN}
              </span>
            </div>
            <div className="dt-err-row" style={{ borderColor: 'rgba(251,191,36,0.25)', background: 'rgba(251,191,36,0.06)' }}>
              <div className="dt-err-dot" style={{ background: '#fbbf24' }}>
                <IconWaveSmall size={9} />
              </div>
              <span style={{ flex: 1, color: '#fde68a', fontFamily: 'JetBrains Mono, monospace', fontSize: 12 }}>
                Fonética
              </span>
              <span style={{ color: '#fde68a', fontFamily: 'JetBrains Mono, monospace', fontSize: 13, fontWeight: 600 }}>
                {phonN}
              </span>
            </div>
          </div>
          <div style={{ marginTop: 14, fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: 'var(--text-faint)', lineHeight: 1.55 }}>
            Tocá los marcadores rojos / ámbar de tus mensajes para ver el detalle.
          </div>
        </div>

        <div style={{ flex: 1 }} />
      </aside>

      {/* Columna principal */}
      <main
        className={`dt-main dt-main-conv conv-arena-root ${gameArena.seed ? 'is-arena' : ''}`}
        style={gameArena.tint ? ({ '--arena-color': gameArena.tint } as CSSProperties) : undefined}
      >
        {/* Festejo al completar TODAS las misiones del escenario (contenido a
            la columna; dispara una sola vez en la transición). */}
        {scenarioMission && (
          <CelebrationLayer type="confetti-cannons" trigger={allObjectivesDone} sound="celebrate-big" />
        )}
        {/* Bonus del escenario cumplido: un escalón sobre el confeti (encargo 104,
            Emmanuel eligió la lluvia dorada). */}
        {scenarioMission && (
          <CelebrationLayer type="bonus-rain" trigger={ctrl.bonusDone} sound="bonus-rain" />
        )}
        {/* Final de clase: el mismo festejo al confirmarse la parte (encargo 104). */}
        {!scenarioMission && subUnit && (
          <CelebrationLayer type="confetti-cannons" trigger={lessonEndTrigger} sound="celebrate-big" />
        )}
        {/* Festejo chico al completar una micro-charla A0 (no-boss). El boss festeja
            aparte con A0Ceremony en su propio flujo de graduación. */}
        {a0Mission && !a0Mission.isBoss && (
          <CelebrationLayer type="sparkles" trigger={a0CelebrateTick} sound="celebrate-small" />
        )}
        {/* Habilidad completada en charla libre (W6): chispas chicas, SIN sonido
            — la charla no se interrumpe; el chip de la burbuja dice cuál. */}
        <CelebrationLayer type="sparkles" trigger={skillCompleteTick} />

        {/* Cierre de la escena: FAB flotante (se ilumina al completar la misión).
            Se oculta con la card de Inspire abierta: a 1024 px caía encima de
            «Usar esto». */}
        {scenarioMission && (
          <MissionFinishFab
            allDone={allObjectivesDone}
            onFinish={leave}
            desktop
            hidden={
              inspire.open ||
              assisted.cardVisible ||
              assisted.deckVisible ||
              !!ctrl.bonusPending ||
              !!ctrl.lessonCompletion
            }
          />
        )}
        {/* Programa con sub-unidades: sin FAB. La parte completa cierra la
            clase con `LessonCompletedCard` (final de clase, #450). */}
        {!scenarioMission && !subUnit && planObjectives.length > 0 && allPlanObjectivesDone && (
          <MissionFinishFab
            allDone
            onFinish={leave}
            desktop
            hidden={inspire.open || assisted.cardVisible || assisted.deckVisible}
          />
        )}

        {shortLeavePrompt && (
          <ShortLeaveSheet onStay={dismissLeaveShort} onLeave={confirmLeaveShort} />
        )}

        {/* Charla asistida: la hoja de instrucciones antes de la primera respuesta modelo. */}
        {assisted.phase === 'intro' && (
          <AssistedIntroSheet
            sceneTitle={assisted.sceneTitle}
            onStart={assisted.start}
            onSolo={assisted.goSolo}
          />
        )}

        {drillErr && (
          <DrillModal
            err={drillErr}
            onClose={() => setDrillErr(null)}
            {...(sessionId ? { sessionId } : {})}
            pauseMainCapture={pauseMainCapture}
            resumeMainCapture={resumeMainCapture}
          />
        )}

        {/* Header */}
        <div className="dt-conv-header">
          <div>
            <div className="dt-topbar__kicker">SESIÓN · EN VIVO</div>
            <div style={{ position: 'relative', display: 'inline-block', marginTop: 4 }}>
              <span className="script" style={{ fontSize: 36, lineHeight: 1, color: 'var(--text)' }}>
                Conversación
              </span>
              <Underline color="var(--lime)" width={180} style={{ marginTop: 2 }} />
            </div>
            {planLabel && <div className="conv-plan-pill">Programa · {planLabel.title}</div>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {/* Actividades vive ahora en el "+" del composer (barra inferior);
                el popover se ancla ahí. El header conserva velocidad + ajustes. */}
            <SpeedQuickControl
              value={settings.speed}
              effective={speedEff}
              onChange={(s) => setSettings((st) => ({ ...st, speed: s }))}
              anchor="right"
              circular
              disabled={ended}
            />
            {/* Variante B: voz del coach | ajustes agrupados en una pill con
                divisor (mismo cluster que el shell móvil). */}
            <div className="conv-head-cluster">
              {/* Toggle de voz del coach: altavoz activo = responde con voz;
                  tachado (lima) = sólo texto, sin TTS. Cambia EN VIVO. */}
              <button
                type="button"
                className={`conv-cluster-btn conv-voice-toggle ${coachMuted ? 'is-muted' : ''}`}
                aria-label={coachMuted ? 'Activar la voz del coach' : 'Silenciar la voz del coach'}
                aria-pressed={coachMuted}
                disabled={ended}
                onClick={toggleCoachMuted}
              >
                {coachMuted ? <IconSpeakerOff size={17} /> : <IconSpeaker size={17} />}
              </button>
              <span className="conv-cluster-div" aria-hidden />
              <button
                type="button"
                className={`conv-cluster-btn ${incognito ? 'has-private-dot' : ''}`}
                aria-label="Ajustes de la charla"
                disabled={ended}
                onClick={() => {
                  inspire.close();
                  setSettingsOpen(true);
                }}
              >
                <IconChatGear size={17} />
              </button>
            </div>
          </div>
        </div>

        {/* Toast efímero del mute (5s, fade vía CSS): al silenciar y al abrir
            una charla ya muteada. El ancla de altura 0 no empuja el layout. */}
        {muteNotice && (
          <div className="conv-mute-toast-anchor">
            <div className="conv-mute-toast" role="status">
              <IconSpeakerOff size={12} />
              <span>El coach responde por texto (gastás menos orbs)</span>
            </div>
          </div>
        )}

        {/* Examen de nivelación: progreso + cierre claro */}
        {examMode && <ExamProgress messages={messages} onFinish={leave} />}

        {/* Badge de charla privada (visible toda la sesión) */}
        {incognito && <PrivateBadge />}

        {/* Estado de conexión (reconectando / caída). El cierre server-side va
            aparte: SessionEndedCard al final del hilo de mensajes. */}
        <ConnectionBanner status={connectionStatus} onReconnect={reconnect} onLeave={leave} />

        {/* Modo juego (plan 47): score pill — nombre + Salir en todos los
            GAME_SEEDS, dificultad salvo describe-it, ronda / ✓ / racha en
            translate-it. En describe-it precede a la SecretWordCard. */}
        {gameArena.seed && (
          <GameModeChip
            seed={gameArena.seed}
            onExit={clearSeed}
            exitDisabled={ended}
            round={gameArena.round?.round ?? null}
            score={gameArena.score}
            scored={gameArena.scored}
            difficulty={gameArena.difficulty}
            band={gameArena.band}
            bandMoment={gameArena.bandMoment}
          />
        )}

        {/* Palabra secreta (juego describe-it): client-only, el coach no la ve. */}
        {activeSeed === 'describe-it' && secretWord && (
          <SecretWordCard word={secretWord} onReroll={rerollSecretWord} disabled={ended} />
        )}

        {/* Tracker de misión en vivo (paridad con móvil; sólo curados con misión) */}
        {scenarioMission && (
          <MissionTracker
            mission={scenarioMission}
            completed={completedObjectives}
            justId={justObjectiveId}
            practiced={practicedObjectives}
            justPracticedId={justPracticedId}
          />
        )}

        {/* Tracker de SUB-UNIDAD en vivo (programa autorado, fase 17.B) */}
        {!scenarioMission && subUnit && (
          <UnitObjectivesTracker
            objectives={subUnitObjectives}
            completed={subUnitCompleted}
            justIdx={justSubObjIdx}
            justTick={subRepTick}
            reps={subUnitReps}
            counts={subUnitCounts}
            subUnitTitle={subUnit.titleEs}
            subUnitOrder={subUnit.order}
            subUnitTotal={subUnit.total}
            review={subUnitReview}
          />
        )}

        {/* Tracker de objetivos de la unidad en vivo (modo plan legacy, fase 16.B) */}
        {!scenarioMission && !subUnit && planObjectives.length > 0 && (
          <UnitObjectivesTracker
            objectives={planObjectives}
            completed={completedPlanObjectives}
            justIdx={justPlanObjectiveIdx}
          />
        )}

        {/* «Armá tu nave»: la pieza de esta charla y cuánto falta (sólo con el recorrido en curso). */}
        <ShipPieceTracker
          piece={shipPiece}
          live={conversationLive({
            scenarioMission,
            completedObjectives,
            subUnit,
            subUnitObjectives,
            subUnitCompleted,
            planObjectives,
            completedPlanObjectives,
          })}
          ask={{ ready: ctrl.shipCanAsk, onBack: ctrl.leaveToShip }}
        />

        {/* Chat */}
        <div
          className={`dt-chat ${assisted.deckVisible ? 'conv-assisted' : ''}`}
          ref={threadRef}
          role="log"
          aria-label="Mensajes de la conversación"
          onScroll={handleThreadScroll}
        >
          <div className="dt-chat-inner">
            <ConversationMessages ctrl={ctrl} />
            {/* El coach no pudo responder (aviso del server o tope de 20 s): debajo
                del mensaje que quedó sin respuesta. Un cierre de la charla la tapa. */}
            {serverEnded == null && replyFailure != null && !ctrl.lessonCompletion ? (
              <ReplyFailedCard kind={replyFailure} onRetry={retryFailedReply} onLeave={leave} />
            ) : null}
            {/* Cierre server-side (idle): countdown de preaviso → card
                terminal, ambos al FINAL del hilo. */}
            {serverEnded != null && serverEnded !== 'lesson-completed' ? (
              <SessionEndedCard reason={serverEnded} onResume={resumeConversation} onLeave={leave} />
            ) : serverClosing ? (
              <SessionClosingCard inSeconds={serverClosing.inSeconds} />
            ) : null}
            {/* Charla asistida: la hoja al final de la charla, en lugar del
                composer (maqueta 1 · Escenario con la hoja de la 2, 16/10); en
                la compu, la botonera al costado de la frase. */}
            {assisted.deckVisible && (
              <AssistedCard
                assisted={assisted}
                voiceId={coach.activeVoiceId ?? FALLBACK_VOICE_ID}
                onDuck={freeChat.setDucked}
                onFinish={leave}
                recorder={recorder}
                holdDisabled={holdDisabled}
              />
            )}
            {/* Autoscroll respetuoso: chip sticky para volver al final. */}
            {threadUnpinned && <JumpToLatestChip onJump={jumpToLatest} />}
          </div>
        </div>

        {/* Card de sugerencias del Inspire (flota arriba del footer). */}
        {/* Charla asistida, después de «Hacelo solo»: la tarjeta «Ahora solo». */}
        {assisted.phase === 'off' && assisted.soloSince !== null && (
          <div className="dt-inspire-wrap">
            <AssistedCard assisted={assisted} voiceId={coach.activeVoiceId ?? FALLBACK_VOICE_ID} />
          </div>
        )}
        {inspire.open && (
          <div className="dt-inspire-wrap">
            <InspireCard
              suggestions={inspire.suggestions}
              idx={inspire.idx}
              loading={inspire.loading}
              error={inspire.error}
              voiceId={coach.activeVoiceId ?? FALLBACK_VOICE_ID}
              onCycle={inspire.cycle}
              onUse={(sug) => sendInspireSuggestion(sug.en)}
              readAloud={inspireReadAloud}
              onRetry={inspire.retry}
              onClose={inspire.close}
            />
          </div>
        )}

        {/* Hint sutil: las palabras del coach son clickeables (descartable, 24 h).
            Se oculta si la card de sugerencias está abierta para no solaparse,
            — and, in English: hidden too during a translate-it game (the arena is busy enough). */}
        <TapWordHint
          hidden={inspire.open || assisted.cardVisible || assisted.deckVisible || gameArena.round != null}
        />

        {/* Red A0 (plan 33 §1.1): chips tap-or-speak del objetivo pendiente. Sólo
            en charla a0 con misión (gate de RENDER, no de montaje del hook). */}
        {a0Mission && a0SuggestionStep && (
          <div className="dt-a0sug-wrap">
            <A0Suggestions
              step={a0SuggestionStep}
              onSend={sendA0Suggestion}
              busy={state === 'thinking' || state === 'speaking' || ended}
            />
          </div>
        )}

        {/* Composer compartido (@orborix/voice-composer), mismo layout que
            mobile: "+" de Actividades + campo con lamparita + mic hold. El
            popover de Actividades se ancla a esta barra y abre hacia arriba. */}
        {/* En la charla asistida el micrófono es el de la hoja (mismo recorder). */}
        {!assisted.deckVisible && (
          <div className="dt-mic-bar">
            <VoiceComposer
              recorder={recorder}
              holdDisabled={holdDisabled}
              micHidden={micHidden}
              disabled={ended || assistedPaused}
              placeholder={
                ended
                  ? COMPOSER_PLACEHOLDER_ENDED
                  : micHidden
                    ? COMPOSER_PLACEHOLDER_TEXT_ONLY
                    : COMPOSER_PLACEHOLDER
              }
              onSendText={sendComposerText}
              labels={COMPOSER_LABELS}
              freeChat={freeChat}
              freeStatus={freeStatus}
              turnClosedTick={ctrl.userTurnTick}
              leading={
                !seedDisabledReason ? (
                  <button
                    type="button"
                    className={`conv-plus-btn ${activeSeed ? 'is-active' : ''}`}
                    disabled={!isUserTurn}
                    onClick={() => {
                      inspire.close();
                      openSeedMenu();
                    }}
                    aria-haspopup="dialog"
                    aria-expanded={seedMenuOpen}
                    aria-label="Actividades"
                    title="Actividades"
                  >
                    <IconPlus size={20} />
                  </button>
                ) : undefined
              }
              fieldStart={
                <InspireButton
                  onClick={inspire.toggleOpen}
                  active={inspire.open}
                  disabled={ended || assisted.phase !== 'off' || (!inspire.open && !isUserTurn)}
                />
              }
            />
            {seedMenuOpen && (
              <DirectionMenu
                variant="popover"
                activeSeed={activeSeed}
                onSelectSeed={selectSeed}
                onScenario={goToScenarios}
                onNews={selectNews}
                onClear={clearSeed}
                onClose={closeSeedMenu}
              />
            )}
          </div>
        )}

        {/* Settings (panel lateral derecho) */}
        {settingsOpen && (
          <SettingsSheet
            settings={settings}
            onChange={setSettings}
            onIntensityChange={setGlobalIntensity}
            onClose={() => setSettingsOpen(false)}
            coach={coach}
            variant="desktop"
            incognito={incognito}
            onToggleIncognito={toggleIncognito}
          />
        )}
      </main>
    </div>
  );
}
